import React from 'react';
import './App.css';
import UniversalHeader from './UniversalHeader';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';

function statusLabel(status) {
  if (status === 'in_progress') return 'In progress';
  if (status === 'completed') return 'Completed';
  return 'Pending';
}

function mapRequestRow(row) {
  const room = row.room_label ? ` · Room ${row.room_label}` : '';
  return {
    id: row.id,
    tenant: row.tenant_name || '—',
    property: `${row.property_name || '—'}${room}`,
    issue: row.issue_description || '—',
    status: statusLabel(row.status),
  };
}

function mapStaffRow(row) {
  return {
    id: row.id,
    name: row.full_name || '—',
    role: row.role || '—',
    email: row.email || '—',
    phone: row.phone || '—',
    openTasks: 0,
    todayTasks: 0,
  };
}

function Staff() {
  const [search, setSearch] = React.useState('');
  const [assignModalOpen, setAssignModalOpen] = React.useState(false);
  const [selectedMember, setSelectedMember] = React.useState(null);
  const [selectedTaskId, setSelectedTaskId] = React.useState('');
  const [assignedTasksByStaff, setAssignedTasksByStaff] = React.useState({});
  const navigate = useNavigate();

  const [staff, setStaff] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [listError, setListError] = React.useState('');

  const [existingTasks, setExistingTasks] = React.useState([]);
  const [tasksLoading, setTasksLoading] = React.useState(false);

  const [toast, setToast] = React.useState(null);

  React.useEffect(() => {
    if (!toast) return undefined;
    const t = window.setTimeout(() => setToast(null), 3500);
    return () => window.clearTimeout(t);
  }, [toast]);

  const loadStaff = React.useCallback(async () => {
    if (!supabase) {
      setListError('Supabase is not configured.');
      setStaff([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setListError('');
    const { data, error } = await supabase
      .from('maintenance_staff')
      .select('id, full_name, role, email, phone, created_at')
      .order('created_at', { ascending: false });

    if (error) {
      setListError(error.message);
      setStaff([]);
    } else {
      setStaff((data || []).map(mapStaffRow));
    }
    setLoading(false);
  }, []);

  React.useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const loadTasksForAssign = React.useCallback(async () => {
    if (!supabase) {
      setExistingTasks([]);
      return;
    }
    setTasksLoading(true);
    const { data, error } = await supabase
      .from('maintenance_requests')
      .select(
        'id, tenant_name, property_name, room_label, issue_description, status'
      )
      .order('created_at', { ascending: false })
      .limit(80);

    if (error) {
      setExistingTasks([]);
    } else {
      setExistingTasks((data || []).map(mapRequestRow));
    }
    setTasksLoading(false);
  }, []);

  const filtered = staff.filter((s) => {
    const term = search.toLowerCase();
    return (
      !term ||
      s.name.toLowerCase().includes(term) ||
      s.role.toLowerCase().includes(term) ||
      s.email.toLowerCase().includes(term)
    );
  });

  const initials = (name) =>
    name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2);

  const handleAssign = (member) => {
    setSelectedMember(member);
    setSelectedTaskId('');
    setAssignModalOpen(true);
    loadTasksForAssign();
  };

  React.useEffect(() => {
    if (!assignModalOpen || !existingTasks.length) return;
    setSelectedTaskId((prev) => prev || existingTasks[0]?.id || '');
  }, [assignModalOpen, existingTasks]);

  const handleEdit = (member) => {
    navigate(`/staff/${member.id}/edit`);
  };

  const handleDelete = async (member) => {
    const ok = window.confirm(`Remove staff member "${member.name}" from the roster?`);
    if (!ok) return;
    if (!supabase) {
      setToast('Supabase is not configured.');
      return;
    }
    const { error } = await supabase.from('maintenance_staff').delete().eq('id', member.id);
    if (error) {
      setToast(error.message);
      return;
    }
    setAssignedTasksByStaff((prev) => {
      const next = { ...prev };
      delete next[member.id];
      return next;
    });
    setToast('Staff member removed.');
    loadStaff();
  };

  const handleAdd = () => {
    navigate('/staff/new');
  };

  const closeAssignModal = () => {
    setAssignModalOpen(false);
    setSelectedMember(null);
    setSelectedTaskId('');
  };

  const confirmAssignTask = () => {
    if (!selectedMember || !selectedTaskId) return;
    const pickedTask = existingTasks.find((t) => t.id === selectedTaskId);
    if (!pickedTask) return;

    setAssignedTasksByStaff((prev) => {
      const prevForMember = prev[selectedMember.id] || [];
      return {
        ...prev,
        [selectedMember.id]: [...prevForMember, pickedTask],
      };
    });

    setToast(`Assigned “${pickedTask.issue}” to ${selectedMember.name}.`);
    closeAssignModal();
  };

  return (
    <div className="staff-root">
      <UniversalHeader />

      <div className="staff-shell">
        <header className="staff-header">
          <div>
            <h1 className="staff-title">Staff</h1>
            <p className="staff-subtitle">
              Manage your maintenance team, their contact details, and assignments.
            </p>
          </div>

          <div className="staff-actions">
            <button type="button" className="btn-ghost staff-btn" onClick={handleAdd}>
              + Add staff
            </button>
          </div>
        </header>

        {listError ? (
          <p className="properties-save-error" role="alert" style={{ marginBottom: 12 }}>
            {listError}
          </p>
        ) : null}

        <section className="staff-toolbar">
          <div className="staff-search">
            <div className="input-shell staff-search-shell">
              <div className="input-icon">🔍</div>
              <input
                className="input-element"
                type="text"
                placeholder="Search by name, role, or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </section>

        <main className="staff-layout">
          <section className="staff-list">
            <div className="staff-list-header">
              <span className="staff-list-label">Maintenance staff</span>
              <span className="staff-list-count">
                {loading ? 'Loading…' : `${filtered.length} team members`}
              </span>
            </div>

            {loading ? (
              <p className="properties-list-empty" style={{ margin: '24px 0' }}>
                Loading staff…
              </p>
            ) : null}

            {!loading && staff.length === 0 ? (
              <p className="properties-list-empty" style={{ margin: '24px 0' }}>
                No staff yet. Run <code>sql/add_staff_supabase.sql</code> in Supabase, then add a
                member.
              </p>
            ) : null}

            <div className="staff-grid">
              {!loading &&
                filtered.map((m) => (
                  <article className="staff-card" key={m.id}>
                    <div className="staff-card-main">
                      <div className="staff-avatar">
                        <span>{initials(m.name)}</span>
                      </div>
                      <div className="staff-main-text">
                        <h2 className="staff-card-name">{m.name}</h2>
                        <p className="staff-card-role">{m.role}</p>
                        <p className="staff-card-contact">
                          {m.email} · {m.phone}
                        </p>
                      </div>
                      <div className="staff-tasks">
                        <span className="staff-tasks-open">
                          {m.openTasks + (assignedTasksByStaff[m.id]?.length || 0)} open
                        </span>
                        <span className="staff-tasks-today">{m.todayTasks} today</span>
                      </div>
                    </div>

                    <div className="staff-card-actions">
                      <button
                        type="button"
                        className="btn-ghost staff-assign-btn"
                        onClick={() => handleAssign(m)}
                      >
                        Assign task
                      </button>
                      <div className="staff-icon-actions">
                        <button
                          type="button"
                          className="properties-icon-btn"
                          aria-label="Edit staff"
                          onClick={() => handleEdit(m)}
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          className="properties-icon-btn properties-icon-btn--danger"
                          aria-label="Delete staff"
                          onClick={() => handleDelete(m)}
                        >
                          🗑
                        </button>
                      </div>
                    </div>

                    {(assignedTasksByStaff[m.id] || []).length > 0 && (
                      <div className="staff-assigned-list">
                        <p className="staff-assigned-title">Assigned from existing tasks</p>
                        <ul>
                          {assignedTasksByStaff[m.id].map((task, index) => (
                            <li key={`${task.id}-${index}`}>
                              <span className="staff-assigned-issue">{task.issue}</span>
                              <span className="staff-assigned-meta">
                                {task.tenant} · {task.property}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </article>
                ))}
            </div>
          </section>
        </main>
      </div>

      {assignModalOpen && selectedMember && (
        <div className="staff-modal-backdrop" role="dialog" aria-modal="true">
          <div className="staff-modal-card">
            <div className="staff-modal-head">
              <h3 className="staff-modal-title">Assign task</h3>
              <button
                type="button"
                className="properties-icon-btn"
                onClick={closeAssignModal}
                aria-label="Close assign task popup"
              >
                ✕
              </button>
            </div>

            <p className="staff-modal-sub">
              Assign an existing maintenance task to <strong>{selectedMember.name}</strong>.
            </p>

            <div className="field-wrapper">
              <label className="field-label" htmlFor="assign-task-select">
                Existing tasks
              </label>
              <div className="input-shell">
                {tasksLoading ? (
                  <p className="properties-list-empty" style={{ margin: '8px 0' }}>
                    Loading tasks…
                  </p>
                ) : existingTasks.length === 0 ? (
                  <p className="properties-list-empty" style={{ margin: '8px 0' }}>
                    No maintenance requests yet.
                  </p>
                ) : (
                  <select
                    id="assign-task-select"
                    className="input-element"
                    value={selectedTaskId}
                    onChange={(e) => setSelectedTaskId(e.target.value)}
                  >
                    {existingTasks.map((task) => (
                      <option key={task.id} value={task.id}>
                        {task.tenant} — {task.issue}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {selectedTaskId && (
              <div className="staff-modal-preview">
                {(() => {
                  const picked = existingTasks.find((task) => task.id === selectedTaskId);
                  if (!picked) return null;
                  return (
                    <>
                      <p className="staff-modal-preview-title">{picked.issue}</p>
                      <p className="staff-modal-preview-meta">
                        {picked.tenant} · {picked.property} · {picked.status}
                      </p>
                    </>
                  );
                })()}
              </div>
            )}

            <div className="staff-modal-actions">
              <button
                type="button"
                className="btn-ghost staff-assign-btn"
                onClick={closeAssignModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary staff-assign-btn"
                onClick={confirmAssignTask}
                disabled={!selectedTaskId || existingTasks.length === 0}
              >
                Assign now
              </button>
            </div>
          </div>
        </div>
      )}

      {toast ? (
        <div className="tenants-toast" role="status">
          {toast}
        </div>
      ) : null}
    </div>
  );
}

export default Staff;
